import { useContext, useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Calendar, Target, CheckCircle, Clock, XCircle, Award, Filter, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import Pagination from '../../../../../../components/Pagination';
import auditProgramService from '../../../../../../services/audit_services/auditProgramService';

const DEFAULT_FILTERS = { search: '', status: '', ordering: 'newest' };

export default function AuditProgramsList() {
  const { authTokens, hasRole } = useContext(AuthContext);
  const { showError } = useToast();

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pending (input) vs applied filters
  const [pendingFilters, setPendingFilters] = useState({ ...DEFAULT_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...DEFAULT_FILTERS });

  const [pagination, setPagination] = useState({ count: 0, page: 1, page_size: 10, total_pages: 0 });

  useEffect(() => {
    fetchAuditPrograms(appliedFilters, pagination.page, pagination.page_size);
  }, [appliedFilters, pagination.page, pagination.page_size]);

  const fetchAuditPrograms = async (filters, page, page_size) => {
    setLoading(true);
    try {
      const result = await auditProgramService.getAuditPrograms(
        authTokens.access,
        { search: filters.search, status: filters.status, ordering: filters.ordering },
        { page, page_size }
      );
      if (result.success) {
        setPrograms(result.data.results || []);
        setPagination(prev => ({
          ...prev,
          count: result.data.count || 0,
          total_pages: result.data.total_pages || 0
        }));
      } else {
        showError(result.error || 'Erreur lors du chargement des programmes');
        setPrograms([]);
      }
    } catch (error) {
      showError('Erreur lors du chargement des programmes');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    setAppliedFilters({ ...pendingFilters });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleReset = () => {
    setPendingFilters({ ...DEFAULT_FILTERS });
    setAppliedFilters({ ...DEFAULT_FILTERS });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = appliedFilters.search || appliedFilters.status || appliedFilters.ordering !== 'newest';

  const getStatusBadge = (status) => {
    const statusConfig = {
      opened:    { bg: 'bg-teal-100',   text: 'text-teal-800',   label: 'Ouvert',   icon: Target },
      closed:    { bg: 'bg-red-100',    text: 'text-red-800',    label: 'Fermé',    icon: XCircle },
      achieved:  { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Atteint',  icon: Award },
      scheduled: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Planifié', icon: Clock },
      completed: { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Complété', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100',    text: 'text-red-800',    label: 'Annulé',   icon: Clock }
    };
    const config = statusConfig[status] || statusConfig.opened;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Programmes d'audit</h1>
          <p className="text-sm text-gray-600 mt-1">Gérer et organiser vos programmes d'audit</p>
        </div>
        {hasRole('AuditManager') && (
          <Link
            to="/dashboard/audit/programs/create"
            className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer un programme
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
          <Filter className="h-4 w-4" />
          <span>Filtres et recherche</span>
          {hasActiveFilters && (
            <span className="ml-2 px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs">Actifs</span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par titre ou portée..."
              value={pendingFilters.search}
              onChange={(e) => setPendingFilters(prev => ({ ...prev, search: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleApply()}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Status filter */}
          <select
            value={pendingFilters.status}
            onChange={(e) => setPendingFilters(prev => ({ ...prev, status: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="opened">Ouvert</option>
            <option value="closed">Fermé</option>
            <option value="achieved">Atteint</option>
          </select>

          {/* Ordering */}
          <select
            value={pendingFilters.ordering}
            onChange={(e) => setPendingFilters(prev => ({ ...prev, ordering: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
          >
            <option value="newest">Plus récent d'abord</option>
            <option value="oldest">Plus ancien d'abord</option>
          </select>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleApply}
            className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm"
          >
            <Search className="h-4 w-4 mr-2" />
            Appliquer
          </button>
          <button
            onClick={handleReset}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Results count */}
      {!loading && (
        <p className="text-sm text-gray-500">
          {pagination.count} programme{pagination.count !== 1 ? 's' : ''} trouvé{pagination.count !== 1 ? 's' : ''}
        </p>
      )}

      {/* Programs List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mx-auto"></div>
              <p className="mt-3 text-gray-600 text-sm">Chargement...</p>
            </div>
          </div>
        ) : programs.length === 0 ? (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun programme d'audit trouvé</p>
            {hasActiveFilters && (
              <button onClick={handleReset} className="mt-3 text-sm text-teal-600 hover:underline">
                Réinitialiser les filtres
              </button>
            )}
            {hasRole('AuditManager') && !hasActiveFilters && (
              <Link
                to="/dashboard/audit/programs/create"
                className="inline-flex items-center mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer votre premier programme
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Détails du programme</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parties impliquées</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Créé par</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {programs.map((program) => (
                  <tr key={program.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{program.title}</p>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(program.date).toLocaleDateString()}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Portée: {Array.isArray(program.scope) ? program.scope.join(', ') : program.scope}
                      </p>
                      <p className="text-xs text-gray-500">Réf: {program.reference}</p>
                    </td>
                    <td className="px-6 py-4">
                      {program.parties.length > 0 ? (
                        <>
                          <p className="text-sm font-medium text-gray-600">{program.parties.length} personnes impliquées</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {program.parties.slice(0, 2).map(p => p.name).join(', ')}
                            {program.parties.length > 2 && ` +${program.parties.length - 2} autres`}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-gray-400">Aucune partie assignée</p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(program.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{program.created_by}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link to={`/dashboard/audit/programs/${program.id}`} className="text-teal-600 hover:text-teal-900" title="Voir les détails">
                          <Eye className="h-4 w-4" />
                        </Link>
                        {hasRole('AuditManager') && program.status === 'opened' && (
                          <Link to={`/dashboard/audit/programs/${program.id}/edit`} className="text-green-600 hover:text-green-900" title="Modifier">
                            <Edit className="h-4 w-4" />
                          </Link>
                        )}
                        {hasRole('AuditManager') && program.status !== 'opened' && (
                          <span className="text-gray-400 cursor-not-allowed" title={`Impossible de modifier (statut: ${program.status})`}>
                            <Edit className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && programs.length > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          pageSize={pagination.page_size}
          onPageSizeChange={(size) => setPagination(prev => ({ ...prev, page_size: size, page: 1 }))}
          totalItems={pagination.count}
        />
      )}
    </div>
  );
}
